// QR Kiosk v2 — KV2-6 track B: the B8 draft-ticket contract (frontend side).
// Ships DARK behind KIOSK_DRAFT_TICKET_ENABLED = false until the bot emits drafts
// (B8, [BOT-SIDE]; MIDAS PM owns the wire contract in API_CONTRACTS.md). The
// dual-carrier mapper isolates carrier A (new artifact type) vs carrier B (reuse
// the existing action_request) so tracks A/B are drop-in for whichever the bot
// ships (spec §6/§7). NO React, NO MUI — pure functions + types only.
import type { ChatMessage, ChatActionRequest } from "../../../shared/chatBot/models/chatBotModels";
import { isValidMachineUuid } from "../dashboard/kioskLiveStatus";
import { MachinePartGroup } from "../../models/serviceReportsModels";
import { findPartTranslationKey } from "../reports/reportUtils";

/** Capability flag (PG-17). FALSE ⇒ mapDraftFromSse / getDraftForMessage return
 *  null unconditionally (first line), so the artifact renderer + create flow are
 *  fully inert platform-wide. Flip ONLY in the §7 flip-commit once B8 is live and
 *  the carrier is confirmed — never in this phase. */
export const KIOSK_DRAFT_TICKET_ENABLED = false;

// Field clamps mirror the create-report form limits (CreateReportV2 / ChangeServiceReport).
const TITLE_MAX = 100;
const DESC_MAX = 2000;
const ALARM_MAX = 100;
const NOTES_MAX = 4000;

/** Normalized draft the kiosk renders + creates from (§6). One draft per message. */
export interface KioskDraftTicket {
  title: string; // required; clamp ≤100 at map time
  description?: string; // clamp ≤2000
  affectedPartCategoryId?: string; // GUID from the /v1/machine-parts taxonomy — REQUIRED for one-tap create
  affectedPartLabel?: string; // human label fallback when the id is absent/unresolvable
  urgency?: number; // honored: 1|2|4 (value-3 hole preserved, D9); anything else → undefined
  alarmCode?: string; // clamp ≤100
  notes?: string; // clamp ≤4000
  openDate?: string; // UTC ISO-8601; absent → "now" at create (form parity)
  machineId: string; // GUID — MUST equal the session machine.id
}

const clampStr = (v: unknown, max: number): string | undefined => {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  if (!t) return undefined;
  return t.length > max ? t.slice(0, max) : t;
};

const normalizeUrgency = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : Number(v);
  return n === 1 || n === 2 || n === 4 ? n : undefined; // value-3 hole preserved (D9)
};

const normalizeOpenDate = (v: unknown): string | undefined => {
  if (typeof v !== "string" || !v.trim()) return undefined;
  const ms = Date.parse(v);
  if (!Number.isFinite(ms)) return undefined; // unparseable → drop
  if (ms > Date.now()) return undefined; // future → drop (form parity)
  return new Date(ms).toISOString();
};

const readId = (a: any, snake: string, camel: string): string | undefined =>
  typeof a?.[snake] === "string" ? a[snake] : typeof a?.[camel] === "string" ? a[camel] : undefined;

/** snake_case/camelCase wire keys → normalized fields, shared by both carriers. */
function normalizeRaw(raw: Record<string, any>, sessionMachineId: string): KioskDraftTicket | null {
  const title = clampStr(raw.title ?? raw.Title, TITLE_MAX);
  if (!title) return null; // title is required for a usable draft

  const machineId = readId(raw, "machine_id", "machineId") ?? "";
  // Machine-echo guard (§6/§8): a draft MUST be for the session machine. Missing or
  // mismatched → discard (cross-machine draft-injection guard).
  if (!isValidMachineUuid(machineId) || machineId.toLowerCase() !== sessionMachineId.toLowerCase()) {
    // eslint-disable-next-line no-console
    console.warn("[kiosk-draft] discarded — machineId missing or ≠ session machine");
    return null;
  }

  return {
    title,
    description: clampStr(raw.description ?? raw.Description, DESC_MAX),
    affectedPartCategoryId: readId(raw, "affected_part_category_id", "affectedPartCategoryId"),
    affectedPartLabel: clampStr(raw.affected_part_label ?? raw.affectedPartLabel, TITLE_MAX),
    urgency: normalizeUrgency(raw.urgency ?? raw.Urgency),
    alarmCode: clampStr(raw.alarm_code ?? raw.alarmCode, ALARM_MAX),
    notes: clampStr(raw.notes ?? raw.Notes, NOTES_MAX),
    openDate: normalizeOpenDate(raw.open_date ?? raw.openDate),
    machineId,
  };
}

/** The two candidate wire carriers (§6). mapDraftFromSse isolates the choice so the
 *  frontend is drop-in for either — the carrier ruling is a MIDAS-PM decision. */
export type DraftCarrier =
  | { kind: "artifact"; artifact: any } // A: { type: "draft_service_report", title, data: {…} }
  | { kind: "action"; actionRequest: ChatActionRequest }; // B: machinery.create_service_report

/** Dual-carrier normalization + validation + machine-echo guard (§6). Flag off →
 *  null. Strings clamped; urgency ∉ {1,2,4} → undefined; openDate unparseable/future
 *  → undefined. One draft per message (extras ignored — recorded limit). */
export function mapDraftFromSse(carrier: DraftCarrier, sessionMachineId: string): KioskDraftTicket | null {
  if (!KIOSK_DRAFT_TICKET_ENABLED) return null;
  if (carrier.kind === "artifact") {
    const a = carrier.artifact;
    if (!a || a.type !== "draft_service_report") return null;
    const data = a.data && typeof a.data === "object" ? a.data : {};
    return normalizeRaw({ title: a.title, ...data }, sessionMachineId);
  }
  // carrier B — reuse the existing action_request (machinery.create_service_report)
  const req = carrier.actionRequest;
  if (!req || req.action_type !== "machinery.create_service_report") return null;
  return normalizeRaw(req.values || {}, sessionMachineId);
}

/** Read the normalized draft associated with a message (from the session store).
 *  Flag off → null (renderer inert). */
export function getDraftForMessage(
  msg: ChatMessage,
  sessionDrafts: Record<string, KioskDraftTicket>
): KioskDraftTicket | null {
  if (!KIOSK_DRAFT_TICKET_ENABLED) return null;
  return sessionDrafts[msg.id] ?? null;
}

/** Pre-create validation (PG-17): title present AND part id resolves against the
 *  loaded taxonomy. The reason drives the inline hint; the fallback is the normal
 *  Report-a-fault form. */
export function validateDraftForCreate(
  draft: KioskDraftTicket,
  machineParts: MachinePartGroup[]
): { ok: true } | { ok: false; reason: "title" | "part" } {
  if (!draft.title || !draft.title.trim()) return { ok: false, reason: "title" };
  if (!draft.affectedPartCategoryId || !findPartTranslationKey(machineParts, draft.affectedPartCategoryId)) {
    return { ok: false, reason: "part" };
  }
  return { ok: true };
}
